package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReviewRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/** Where the submit flow stands (drives the button/banners on the screen). */
enum class ReviewSubmitStatus {
    /** Real (non-anonymous) account required — the route sends the user to sign-in. */
    SignInRequired,
    Failed,
    Submitted,
}

/** UI state for Write a Review — business header + submit progress; the form itself is local state. */
data class WriteReviewUiState(
    val loading: Boolean = true,
    val business: Business? = null,
    val error: Boolean = false,
    val submitting: Boolean = false,
    val submitStatus: ReviewSubmitStatus? = null,
)

/**
 * Loads the target [Business] (header) and submits the finished [ReviewDraft] to [ReviewRepository]
 * (`reviews/{uid}_{businessId}`; re-review edits in place; guests get [ReviewSubmitStatus.SignInRequired]).
 * The business's rating/reviewCount recompute server-side via the deployed `onReviewWritten` Function.
 */
class WriteReviewViewModel(
    private val businessRepository: BusinessRepository,
    private val reviewRepository: ReviewRepository,
    private val analytics: AnalyticsTracker,
    private val businessId: String,
) : ViewModel() {

    private val _state = MutableStateFlow(WriteReviewUiState())
    val state: StateFlow<WriteReviewUiState> = _state.asStateFlow()

    init {
        load()
    }

    fun retry() {
        _state.value = WriteReviewUiState(loading = true)
        load()
    }

    fun submit(draft: ReviewDraft) {
        if (_state.value.submitting) return
        viewModelScope.launch {
            _state.value = _state.value.copy(submitting = true, submitStatus = null)
            val result = reviewRepository.submitReview(
                businessId = businessId,
                rating = draft.rating,
                comment = draft.comment,
                tags = draft.highlightTagKeys,
            )
            _state.value = _state.value.copy(
                submitting = false,
                submitStatus = when (result) {
                    is DataResult.Success -> {
                        analytics.track(
                            AnalyticsEvents.REVIEW_SUBMIT,
                            mapOf(AnalyticsParams.BUSINESS_ID to businessId),
                        )
                        ReviewSubmitStatus.Submitted
                    }
                    is DataResult.Failure ->
                        if (result.error is AppError.Unauthorized) ReviewSubmitStatus.SignInRequired
                        else ReviewSubmitStatus.Failed
                },
            )
        }
    }

    /** Clears a shown failure banner after the UI has consumed it. */
    fun consumeSubmitStatus() {
        _state.value = _state.value.copy(submitStatus = null)
    }

    private fun load() {
        viewModelScope.launch {
            _state.value = when (val result = businessRepository.getBusiness(businessId)) {
                is DataResult.Success -> WriteReviewUiState(loading = false, business = result.data)
                is DataResult.Failure -> WriteReviewUiState(loading = false, error = true)
            }
        }
    }
}
